/**
 * Anonymization utilities for GDPR compliance
 * Provides consistent hashing for user identifiers
 * Uses the same hash function as variant assignment for consistency
 */

/**
 * Creates a consistent hash from a string
 * Same algorithm as used in variant-assignment.service.ts
 * The same input will always produce the same output, but the original cannot be recovered
 * 
 * @param input - The string to hash (e.g., user ID)
 * @returns A positive integer hash value
 */
export function createHash(input: string): number {
  let hash = 0;
  if (input.length === 0) return hash;

  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    // eslint-disable-next-line no-bitwise
    hash = (hash << 5) - hash + char;
    // eslint-disable-next-line no-bitwise
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Ensure positive number
  return Math.abs(hash);
}

/**
 * Anonymizes a user ID by hashing it with a prefix
 * The prefix helps identify that this is an anonymized ID
 * 
 * @param userId - The original user ID
 * @returns An anonymized user ID (e.g., "anon_a1b2c3d4")
 */
export function anonymizeUserId(userId: string): string {
  const hash = createHash(userId);
  return `anon_${hash.toString(16)}`;
}

/**
 * Checks if a user ID is already anonymized
 * 
 * @param userId - The user ID to check
 * @returns true if the ID starts with "anon_"
 */
export function isAnonymizedUserId(userId: string): boolean {
  return userId.startsWith('anon_');
}
